import { CELL, GRID_SIZE, SKY_BAND, SPAN } from '../game/constants';
import { cx, cy, key, parseKey } from '../game/geometry';
import { network, towerRoles } from '../game/network';
import type { Network, Point, Puzzle } from '../game/types';
import { canHover, prefersReducedMotion } from './motion';
import { SPRITE, TERRAIN_VARIANTS, buildDefs } from './sprites';
import { RATE, SurgeAnimator } from './surge';
import { clear, el } from './svg';
import { attachPoint, wirePath } from './wires';

export interface RenderResult {
  net: Network;
  /** How long the surge will take to finish, in ms. */
  maxDelay: number;
}

/** What a render should animate beyond the diff — one-shot, from the tap site. */
export interface RenderOptions {
  /** The tower this tap raised: it settles. Restores and re-renders never replay it. */
  justBuilt?: Point;
  /** The tower this tap removed: its sprite is lifted out instead of vanishing. */
  justRemoved?: Point;
}

/**
 * The board's tempos (STYLEGUIDE §9: one clock). Each is defined here once
 * and published to the stylesheet in mount(), so the CSS animation and the
 * timer that cleans up after it cannot drift apart.
 */
const RANGE_PULSE_MS = 900;
const DENY_PULSE_MS = 200;

/**
 * How long a touch has to rest on a cell before it counts as a look rather
 * than a tap. Long enough not to fire during a normal press, short enough
 * that the free look feels free.
 */
const LONG_PRESS_MS = 350;

/** Declared as properties, not methods, so they can be safely destructured. */
export interface BoardCallbacks {
  /** A cell was tapped. Blocked cells report too — the UI explains why. */
  onCell: (p: Point) => void;
  /**
   * Power reached a tower, mid-surge. `order` counts them in arrival order so
   * the sound can climb as the line lights up.
   */
  onEnergized?: (order: number) => void;
  /** A house got its lights, mid-surge. */
  onHouseLit?: (order: number) => void;
}

/**
 * Draws the board and turns taps into game events.
 *
 * Holds one piece of state of its own: which wires, towers and houses were
 * live on the previous frame. That's what separates "already lit, draw it
 * lit" from "just lit, animate the surge into it".
 */
export class BoardRenderer {
  private readonly svg: SVGSVGElement;
  private readonly hoverable = canHover();
  private readonly surge: SurgeAnimator;

  /** Live, not cached: the settings switch can flip mid-session. */
  private get reduceMotion(): boolean {
    return prefersReducedMotion();
  }

  private gTerrain!: SVGGElement;
  private gFx!: SVGGElement;
  private gReference!: SVGGElement;
  private gHover!: SVGGElement;
  private gWires!: SVGGElement;
  private gSprites!: SVGGElement;
  private gHits!: SVGGElement;
  private gCursor!: SVGGElement;

  private prevLiveWires = new Set<string>();
  private prevLiveTowers = new Set<string>();
  private prevLitHouses = new Set<string>();

  private puzzle: Puzzle | null = null;
  private callbacks: BoardCallbacks | null = null;

  /** Cells no tower can stand on — terrain, the plant, the houses. */
  private blockedCells = new Set<string>();
  /** Where the keyboard survey cursor sits, or `null` while it's hidden. */
  private cursor: Point | null = null;
  /** A long press opened a preview, so the click that follows it isn't a tap. */
  private previewing = false;

  constructor(svg: SVGSVGElement) {
    this.svg = svg;
    this.surge = new SurgeAnimator(
      () => this.gWires,
      () => this.reduceMotion,
    );
    this.bindKeyboard();
    // Where the long press is the free look, the system's own long-press
    // menu is in the way of it.
    if (!this.hoverable) {
      this.svg.addEventListener('contextmenu', (e) => e.preventDefault());
    }
  }

  /** Tear down the old board and lay out a new grid. */
  mount(puzzle: Puzzle, callbacks: BoardCallbacks): void {
    this.puzzle = puzzle;
    this.callbacks = callbacks;
    this.surge.clear();
    this.resetSurgeMemory();

    this.svg.setAttribute(
      'viewBox',
      `0 -${SKY_BAND} ${GRID_SIZE * CELL} ${GRID_SIZE * CELL + SKY_BAND}`,
    );

    // Publish the tempos where both the SVG and the HTML HUD (a sibling of
    // the SVG, so vars on the SVG itself would never reach it) can read them.
    const host = this.svg.parentElement ?? this.svg;
    host.style.setProperty('--cell-ms', `${RATE * CELL}ms`);
    host.style.setProperty('--range-pulse', `${RANGE_PULSE_MS}ms`);
    host.style.setProperty('--deny-pulse', `${DENY_PULSE_MS}ms`);

    clear(this.svg);
    buildDefs(this.svg, !this.reduceMotion);

    this.gTerrain = el('g', { id: 'terrain' }, this.svg);
    this.gFx = el('g', { id: 'fx' }, this.svg);
    // The engineers' drawing sits under the player's own wires, so a live
    // circuit always reads on top of a plan (STYLEGUIDE §5).
    this.gReference = el('g', { id: 'reference' }, this.svg);
    this.gHover = el('g', { id: 'hover' }, this.svg);
    this.gWires = el('g', { id: 'wires' }, this.svg);
    this.gSprites = el('g', { id: 'sprites' }, this.svg);
    this.gHits = el('g', { id: 'hits' }, this.svg);
    this.gCursor = el('g', { id: 'cursor' }, this.svg);

    this.cursor = null;
    this.drawTerrain(puzzle);
    this.buildHits(puzzle);
  }

  /**
   * Re-issue the defs after a motion-setting change. The board's only
   * free-running animations — the crossing giant's beacon and the water drift
   * — are SMIL loops living in the defs, out of reach of the stylesheet flag.
   */
  refreshMotion(): void {
    if (!this.puzzle) return;
    this.svg.querySelector('defs')?.remove();
    buildDefs(this.svg, !this.reduceMotion);
  }

  /** Forget what was lit, so the next frame replays the surge from scratch. */
  private resetSurgeMemory(): void {
    this.prevLiveWires = new Set();
    this.prevLiveTowers = new Set();
    this.prevLitHouses = new Set();
  }

  /** Cancel any in-flight surge. */
  cancelPending(): void {
    this.surge.clear();
  }

  /** Schedule work on the surge's timeline, so a later tap cancels it too. */
  schedule(fn: () => void, ms: number): void {
    this.surge.later(fn, ms);
  }

  private drawTerrain(puzzle: Puzzle): void {
    clear(this.gTerrain);
    // sky band: HUD strip + headroom for the giants
    el(
      'rect',
      { x: 0, y: -SKY_BAND, width: GRID_SIZE * CELL, height: SKY_BAND, fill: 'var(--ink)' },
      this.gTerrain,
    );
    // a single flat land field under a hairline survey grid — a map, not a checkerboard
    el(
      'rect',
      { x: 0, y: 0, width: GRID_SIZE * CELL, height: GRID_SIZE * CELL, fill: 'var(--land)' },
      this.gTerrain,
    );
    for (let i = 1; i < GRID_SIZE; i++) {
      el(
        'path',
        {
          d: `M${i * CELL} 0 V${GRID_SIZE * CELL} M0 ${i * CELL} H${GRID_SIZE * CELL}`,
          stroke: 'var(--grid-line)',
          'stroke-width': 1,
        },
        this.gTerrain,
      );
    }
    for (const [k, t] of Object.entries(puzzle.obstacles)) {
      const p = parseKey(k);
      // each tile picks one of the drawn variants, so terrain drifts like a map
      const variant = (p.r * 7 + p.c * 13) % TERRAIN_VARIANTS;
      el(
        'use',
        { href: (t === 'water' ? SPRITE.water : SPRITE.mtn) + variant, x: cx(p), y: cy(p) },
        this.gTerrain,
      );
    }
  }

  private buildHits(puzzle: Puzzle): void {
    clear(this.gHits);
    this.blockedCells = new Set();
    for (let r = 0; r < GRID_SIZE; r++) {
      for (let c = 0; c < GRID_SIZE; c++) {
        const p = { r, c };
        const k = key(p);
        const blocked =
          Boolean(puzzle.obstacles[k]) ||
          k === key(puzzle.plant) ||
          puzzle.houses.some((h) => key(h) === k);
        if (blocked) this.blockedCells.add(k);
        const hit = el(
          'rect',
          {
            x: c * CELL,
            y: r * CELL,
            width: CELL,
            height: CELL,
            class: 'cell-hit' + (blocked ? ' blocked' : ''),
          },
          this.gHits,
        );
        hit.addEventListener('click', () => {
          // A press that opened a preview is spent on the preview.
          if (this.previewing) {
            this.previewing = false;
            return;
          }
          this.callbacks?.onCell(p);
        });
        if (blocked) continue;
        if (this.hoverable) {
          hit.addEventListener('pointerenter', () => this.onHoverEnter?.(p));
          hit.addEventListener('pointerleave', () => this.clearGhost());
        } else {
          this.bindLongPress(hit, p);
        }
      }
    }
  }

  /**
   * Touch has no hover, so the free look is a long press: hold a buildable
   * cell to see the marker, the span and the price, then lift without
   * building. Pillar 3 puts the cost of a plan at zero, and without this it
   * costs two taps on a phone — which is the tax the retired move counter
   * used to charge for exactly the same thing (GDD §9).
   */
  private bindLongPress(hit: SVGElement, p: Point): void {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const cancel = (): void => {
      if (timer) clearTimeout(timer);
      timer = null;
    };

    hit.addEventListener('pointerdown', () => {
      // Each gesture starts clean, which also clears a flag stranded by a
      // press that wandered off the cell and never became a click.
      this.previewing = false;
      cancel();
      timer = setTimeout(() => {
        timer = null;
        // Clear first, so what is counted is what THIS press drew. A ghost
        // left behind by the keyboard survey cursor would otherwise look like
        // a preview and swallow a tap the controller never previewed.
        this.clearGhost();
        this.onHoverEnter?.(p);
        // Only a preview that actually drew swallows the tap: on a cell the
        // controller declined to ghost, the press stays an ordinary tap.
        this.previewing = this.gHover.childElementCount > 0;
      }, LONG_PRESS_MS);
    });

    /**
     * `expectClick` is the whole subtlety: a press lifted on the cell is
     * followed by a click that has to be swallowed, so the flag must outlive
     * it. A press that is cancelled or wanders off produces no click at all,
     * so leaving the flag set would swallow the *next* tap instead — including
     * a tap on blocked terrain, which has a click handler but no pointerdown
     * to reset it.
     */
    const release = (expectClick: boolean): void => {
      cancel();
      if (!this.previewing) return;
      this.clearGhost();
      if (!expectClick) this.previewing = false;
    };
    hit.addEventListener('pointerup', () => release(true));
    hit.addEventListener('pointercancel', () => release(false));
    hit.addEventListener('pointerleave', () => release(false));
  }

  /** Set by the controller: decides whether a ghost tower should appear. */
  onHoverEnter: ((p: Point) => void) | null = null;

  /**
   * A thin dashed survey marker on a valid placement target — not a fill, not
   * a glow — with the span it would cover and, when the controller supplies
   * one, what the plan would cost.
   */
  showGhost(p: Point, cost?: string): void {
    clear(this.gHover);
    el(
      'rect',
      {
        x: p.c * CELL + 6,
        y: p.r * CELL + 6,
        width: CELL - 12,
        height: CELL - 12,
        rx: 2,
        class: 'survey-marker',
      },
      this.gHover,
    );
    // The span this tower would cover — the pulse's square, quieter and
    // static: a plan costs nothing, so it shows before the tap.
    el('rect', { ...this.spanBox(p), rx: 2, class: 'span-preview' }, this.gHover);

    if (!cost) return;
    // The price rides the marker in steel. Delivered power is the only number
    // allowed light (STYLEGUIDE §1), and a plan is not delivered. It sits
    // above the cell, or below it on the top row where the sky band is.
    const label = el(
      'text',
      {
        x: Math.min(GRID_SIZE * CELL - 44, Math.max(44, cx(p))),
        y: p.r > 0 ? p.r * CELL - 14 : CELL + 34,
        class: 'plan-cost',
      },
      this.gHover,
    );
    label.textContent = cost;
  }

  clearGhost(): void {
    clear(this.gHover);
  }

  /**
   * Lay the engineers' drawing on the board: the reference layout as a
   * dashed steel diagram with open squares for its towers.
   *
   * A drawing, not a circuit — no glow, no surge, no sound (STYLEGUIDE §5).
   * The player's own line stays the only live thing on the board, and this
   * only ever appears on a grid whose race is over and which they finished
   * (GDD §4.5).
   */
  showReference(towers: Point[]): void {
    const puzzle = this.puzzle;
    if (!puzzle) return;
    clear(this.gReference);

    const net = network(puzzle, towers);
    const roles = towerRoles(puzzle, net);

    for (const e of net.edges) {
      const d = wirePath(
        attachPoint(e.a, roles[e.ai], cx(e.b)),
        attachPoint(e.b, roles[e.bi], cx(e.a)),
      );
      el('path', { d, class: 'ref-wire' }, this.gReference);
    }
    for (const hl of net.houseLinks) {
      if (!hl.node || hl.nodeIdx === null) continue;
      const d = wirePath(
        attachPoint(hl.node, roles[hl.nodeIdx], cx(hl.h)),
        attachPoint(hl.h, 'house', cx(hl.node)),
      );
      el('path', { d, class: 'ref-wire' }, this.gReference);
    }
    for (const t of towers) {
      el(
        'rect',
        { x: cx(t) - 13, y: cy(t) - 13, width: 26, height: 26, rx: 2, class: 'ref-node' },
        this.gReference,
      );
    }
  }

  clearReference(): void {
    // Tolerates being called before the first mount: starting a session clears
    // the drawing, and the first session starts before there is a board.
    if (this.gReference) clear(this.gReference);
  }

  /** Is the reference drawing currently on the board? */
  get showingReference(): boolean {
    return (this.gReference?.childElementCount ?? 0) > 0;
  }

  /** The square of cells within span of `p`, clipped to the board. */
  private spanBox(p: Point): { x: number; y: number; width: number; height: number } {
    const x0 = Math.max(0, p.c - SPAN) * CELL;
    const y0 = Math.max(0, p.r - SPAN) * CELL;
    const x1 = Math.min(GRID_SIZE, p.c + SPAN + 1) * CELL;
    const y1 = Math.min(GRID_SIZE, p.r + SPAN + 1) * CELL;
    return { x: x0 + 3, y: y0 + 3, width: x1 - x0 - 6, height: y1 - y0 - 6 };
  }

  /** One fading ping showing a new tower's reach. */
  pulseRange(p: Point): void {
    if (this.reduceMotion) return;
    const rect = el('rect', { ...this.spanBox(p), rx: 2, class: 'range-pulse' }, this.gFx);
    // +50: removal waits out the fade rather than racing it.
    setTimeout(() => rect.remove(), RANGE_PULSE_MS + 50);
  }

  /** One brief steel pulse on a refused cell — the hum's visual half. */
  pulseDenied(p: Point): void {
    if (this.reduceMotion) return;
    const rect = el(
      'rect',
      {
        x: p.c * CELL + 2,
        y: p.r * CELL + 2,
        width: CELL - 4,
        height: CELL - 4,
        rx: 2,
        class: 'deny-pulse',
      },
      this.gFx,
    );
    setTimeout(() => rect.remove(), DENY_PULSE_MS + 50);
  }

  // ---------- keyboard ----------

  /**
   * The board plays from the keyboard too: arrows move a survey cursor, Enter
   * or Space taps the cell under it. Same verb, same rules — the cursor is an
   * input method, not a second mechanic (GDD §3.1).
   */
  private bindKeyboard(): void {
    // Every handler waits for mount() — before it there are no groups to draw
    // into and no grid to move around.
    this.svg.addEventListener('keydown', (e) => {
      if (this.puzzle) this.onKey(e);
    });
    // Reveal the cursor for keyboard focus only — a click focuses the board
    // too, and a pointer player shouldn't grow an amber square.
    this.svg.addEventListener('focus', () => {
      if (this.puzzle && this.svg.matches(':focus-visible')) {
        this.moveCursor(this.cursor ?? this.centre());
      }
    });
    this.svg.addEventListener('blur', () => {
      if (this.puzzle) this.hideCursor();
    });
  }

  private centre(): Point {
    return { r: Math.floor(GRID_SIZE / 2), c: Math.floor(GRID_SIZE / 2) };
  }

  private onKey(e: KeyboardEvent): void {
    const dir: Record<string, [number, number]> = {
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
    };
    if (e.key in dir) {
      e.preventDefault();
      const [dr, dc] = dir[e.key];
      const from = this.cursor;
      // The first arrow press reveals the cursor where it last was; only a
      // visible cursor moves.
      const to = from
        ? {
            r: Math.min(GRID_SIZE - 1, Math.max(0, from.r + dr)),
            c: Math.min(GRID_SIZE - 1, Math.max(0, from.c + dc)),
          }
        : this.centre();
      this.moveCursor(to);
    } else if ((e.key === 'Enter' || e.key === ' ') && this.cursor) {
      e.preventDefault();
      this.callbacks?.onCell(this.cursor);
      this.previewAt(this.cursor);
    } else if (e.key === 'Escape' && this.cursor) {
      this.hideCursor();
    }
  }

  private moveCursor(p: Point): void {
    this.cursor = p;
    clear(this.gCursor);
    el(
      'rect',
      {
        x: p.c * CELL + 2,
        y: p.r * CELL + 2,
        width: CELL - 4,
        height: CELL - 4,
        class: 'key-cursor',
      },
      this.gCursor,
    );
    this.previewAt(p);
  }

  /** Same plan preview the pointer gets: ghost and span, valid cells only. */
  private previewAt(p: Point): void {
    this.clearGhost();
    if (!this.blockedCells.has(key(p))) this.onHoverEnter?.(p);
  }

  private hideCursor(): void {
    this.cursor = null;
    clear(this.gCursor);
    this.clearGhost();
  }

  /**
   * Sound the surge in the order power actually arrives, so a long line reads
   * as a rising run rather than a cluster of identical blips. When motion is
   * reduced the visuals are instant, so the audio compresses with them: the
   * run's destination rung once, one bell above it — never the whole run as a
   * chord (STYLEGUIDE §9).
   */
  private scheduleSurgeSounds(towers: number[], houses: number[]): void {
    const { onEnergized, onHouseLit } = this.callbacks ?? {};
    const lit = houses.filter((d) => Number.isFinite(d));
    if (this.reduceMotion) {
      if (towers.length && onEnergized) onEnergized(towers.length - 1);
      if (lit.length && onHouseLit) onHouseLit(Math.max(0, towers.length - 1));
      return;
    }
    if (onEnergized) {
      towers
        .sort((a, b) => a - b)
        .forEach((delay, i) => this.surge.later(() => onEnergized(i), delay));
    }
    if (onHouseLit) {
      lit.sort((a, b) => a - b).forEach((delay, i) => this.surge.later(() => onHouseLit(i), delay));
    }
  }

  /** Redraw wires and sprites for the current layout, animating anything newly live. */
  render(towers: Point[], opts: RenderOptions = {}): RenderResult {
    const puzzle = this.puzzle;
    if (!puzzle) throw new Error('BoardRenderer.render() before mount()');

    this.surge.clear();
    this.retireTower(opts.justRemoved);
    clear(this.gWires);
    clear(this.gSprites);

    const net = network(puzzle, towers);
    const roles = towerRoles(puzzle, net);
    let maxDelay = 0;

    const liveWireKeys = new Set<string>();
    const liveTowerKeys = new Set<string>();
    const litHouseKeys = new Set<string>();

    for (const e of net.edges) {
      const d = wirePath(
        attachPoint(e.a, roles[e.ai], cx(e.b)),
        attachPoint(e.b, roles[e.bi], cx(e.a)),
      );
      const wk = key(e.a) + '|' + key(e.b);
      if (e.live) liveWireKeys.add(wk);
      const isNew = e.live && !this.prevLiveWires.has(wk);
      const path = el(
        'path',
        { d, class: e.live && !isNew ? 'wire-live' : 'wire-dead' },
        this.gWires,
      );
      if (isNew) {
        const from = net.dist[e.ai] <= net.dist[e.bi] ? e.ai : e.bi;
        const delay = net.dist[from] * RATE;
        maxDelay = Math.max(maxDelay, delay + e.len * RATE);
        this.surge.later(() => path.setAttribute('class', 'wire-live'), delay);
        this.surge.launchSpark(d, e.len, from === e.bi, delay);
      }
    }

    for (const hl of net.houseLinks) {
      if (!hl.node || hl.nodeIdx === null) continue;
      const d = wirePath(
        attachPoint(hl.node, roles[hl.nodeIdx], cx(hl.h)),
        attachPoint(hl.h, 'house', cx(hl.node)),
      );
      const hk = 'h' + key(hl.h);
      if (hl.lit) litHouseKeys.add(hk);
      const isNew = hl.lit && !this.prevLitHouses.has(hk);
      const path = el(
        'path',
        { d, class: hl.lit && !isNew ? 'wire-live' : 'wire-dead' },
        this.gWires,
      );
      if (isNew) {
        const delay = net.dist[hl.nodeIdx] * RATE;
        maxDelay = Math.max(maxDelay, hl.arrive * RATE);
        this.surge.later(() => path.setAttribute('class', 'wire-live'), delay);
        this.surge.launchSpark(d, hl.wireLen, false, delay);
      }
    }

    // When each newly-live tower and house lights up, in ms from now.
    const towerArrivals: number[] = [];
    const houseArrivals: number[] = [];

    // sprites are painted back-to-front so nearer rows overlap farther ones
    const sprites: { r: number; make: () => void }[] = [];

    sprites.push({
      r: puzzle.plant.r,
      make: () =>
        el(
          'use',
          { href: SPRITE.plant, x: cx(puzzle.plant), y: cy(puzzle.plant), class: 'plant-sprite' },
          this.gSprites,
        ),
    });

    towers.forEach((t, i) => {
      const idx = i + 1;
      const live = net.energized.has(idx);
      const role = roles[idx];
      if (live) liveTowerKeys.add(key(t));
      const isNew = live && !this.prevLiveTowers.has(key(t));
      if (isNew) towerArrivals.push(net.dist[idx] * RATE);
      // The §8.2 settle plays only on the sprite this tap raised: the next
      // render rebuilds the node without the class, so it can never replay.
      const fresh = opts.justBuilt?.r === t.r && opts.justBuilt?.c === t.c;
      sprites.push({
        r: t.r,
        make: () => {
          const u = el(
            'use',
            {
              href: role === 'plant' ? SPRITE.plant : SPRITE[role],
              x: cx(t),
              y: cy(t),
              class: 'tower' + (live && !isNew ? ' energized' : '') + (fresh ? ' just-built' : ''),
            },
            this.gSprites,
          );
          if (isNew) this.surge.later(() => u.classList.add('energized'), net.dist[idx] * RATE);
        },
      });
    });

    for (const hl of net.houseLinks) {
      const hk = 'h' + key(hl.h);
      const isNew = hl.lit && !this.prevLitHouses.has(hk);
      if (isNew) houseArrivals.push(hl.arrive * RATE);
      sprites.push({
        r: hl.h.r,
        make: () => {
          const u = el(
            'use',
            {
              href: SPRITE.house,
              x: cx(hl.h),
              y: cy(hl.h),
              class: 'house-sprite' + (hl.lit && !isNew ? ' lit' : ''),
            },
            this.gSprites,
          );
          // The windows ride their own overlay so the glow can belong to them
          // alone — a filter on the full house would halo the silhouette.
          const w = el(
            'use',
            {
              href: SPRITE.houseGlow,
              x: cx(hl.h),
              y: cy(hl.h),
              class: 'house-glow' + (hl.lit && !isNew ? ' lit' : ''),
            },
            this.gSprites,
          );
          if (isNew) {
            this.surge.later(() => {
              u.classList.add('lit', 'just-lit');
              w.classList.add('lit', 'just-lit');
            }, hl.arrive * RATE);
          }
        },
      });
    }

    sprites.sort((a, b) => a.r - b.r).forEach((s) => s.make());
    this.scheduleSurgeSounds(towerArrivals, houseArrivals);

    this.prevLiveWires = liveWireKeys;
    this.prevLiveTowers = liveTowerKeys;
    this.prevLitHouses = litHouseKeys;

    return { net, maxDelay: this.reduceMotion ? 0 : maxDelay };
  }

  /**
   * Lift the removed tower's sprite out before the teardown wipes it, so
   * demolition gets the settle reversed (STYLEGUIDE §8.2). Skipped under
   * reduced motion: the stylesheet kill switch cannot clear an orphaned node,
   * and a frozen ghost is worse than an instant disappearance.
   */
  private retireTower(p?: Point): void {
    if (!p || this.reduceMotion) return;
    const gone = this.gSprites.querySelector(`use.tower[x="${cx(p)}"][y="${cy(p)}"]`);
    if (!gone) return;
    this.gFx.appendChild(gone);
    gone.classList.remove('just-built');
    gone.classList.add('tower-out');
    // A plain timeout, not surge.later(): the fade must outlive the next
    // tap's surge.clear() or an invisible node would strand in the fx layer.
    setTimeout(() => gone.remove(), RATE * CELL + 60);
  }
}
